import { useState } from 'react'
import { Lightbulb, Layers, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { useGroupingSuggestions } from '@/features/cases/hooks/useCaseGrouping'
import { AlertLinker } from '@/features/cases/components/AlertLinker'
import type { GroupingSuggestion } from '@/features/cases/types'
import type { AlertSummary } from '../../types'

interface Props {
  alertId: string
  /**
   * The anchoring alert this suggestions panel is hosted on.
   * Optional — when provided, the AlertLinker's Create panel seeds
   * its title / summary / priority / queue from this alert (the
   * suggestion endpoint only returns alert ids, not full
   * summaries, so a single anchor is the most useful prefill we
   * can provide without an extra bulk fetch). The bundle's true
   * size is still communicated via the alertIds prop on
   * AlertLinker, so the title's "(N alerts)" suffix is correct.
   */
  anchorAlert?: AlertSummary
}

/**
 * AF-127 — Suggested groupings panel.
 *
 * Renders the top-5 server-computed grouping suggestions for the current
 * alert. Each card lists why the backend thinks these alerts belong together
 * (same customer 24h, shared device, shared IP, shared counterparty NUBAN)
 * and exposes a single "Group these N alerts" CTA that opens the AlertLinker
 * pre-loaded with the suggestion's alert IDs (plus the current alert).
 *
 * The "shared X" reason strings are server-rendered — we never re-derive
 * them client-side (per technical notes).
 */
export function SuggestedGroupings({ alertId, anchorAlert }: Props) {
  const { data, isLoading, isError } = useGroupingSuggestions(alertId)
  const [active, setActive] = useState<GroupingSuggestion | null>(null)
  const [linkerMode, setLinkerMode] = useState<'pick' | 'create'>('create')

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb size={14} aria-hidden /> Suggested groupings
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (isError || !data || data.length === 0) {
    // Suggestions are an enhancement — render nothing on empty/error so the
    // detail page doesn't gain noise.
    return null
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb size={14} aria-hidden />
            Suggested groupings
            <span className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[0.625rem] font-normal text-muted-fg">
              {data.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col gap-2">
            {data.map((sug) => (
              <li
                key={sug.id}
                className="rounded-md border border-border bg-card transition-colors hover:border-primary/40"
              >
                <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {sug.reasons.map((r) => (
                        <Badge
                          key={r.code}
                          variant="info"
                          className="font-medium"
                          title={r.detail ?? r.label}
                        >
                          {r.label}
                          {r.detail && (
                            <span className="font-mono text-[0.625rem] opacity-80">
                              {' '}
                              · {r.detail}
                            </span>
                          )}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-1.5 text-xs text-muted-fg">
                      <Layers size={10} className="mr-1 inline" aria-hidden />+
                      {sug.alert_ids.length} alert
                      {sug.alert_ids.length === 1 ? '' : 's'} match
                      <span className="ml-2 font-mono">conf {sug.confidence}</span>
                      {sug.candidate_case && (
                        <span className="ml-2">
                          ·{' '}
                          <span className="text-fg">
                            existing case {sug.candidate_case.case_number}
                          </span>
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {sug.candidate_case && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setActive(sug)
                          setLinkerMode('pick')
                        }}
                      >
                        Add to {sug.candidate_case.case_number}
                        <ChevronRight size={12} aria-hidden />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => {
                        setActive(sug)
                        setLinkerMode(sug.candidate_case ? 'pick' : 'create')
                      }}
                    >
                      {sug.candidate_case ? 'Group…' : 'Create case'}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <AlertLinker
        key={active?.id ?? 'none'}
        open={active !== null}
        onOpenChange={(open) => {
          if (!open) setActive(null)
        }}
        defaultMode={linkerMode}
        alertIds={active ? [alertId, ...active.alert_ids] : []}
        // Pass the anchoring alert summary so the Create panel
        // gets a sensible title/summary/priority/queue baseline.
        // The bundle is bigger than one alert; derivePrefill uses
        // alertIds.length for the title's "(N alerts)" count.
        prefillAlerts={anchorAlert ? [anchorAlert] : undefined}
        onLinked={() => setActive(null)}
      />
    </>
  )
}
